import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';

const InsightArticle3 = () => {
  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      <article className="pt-40 pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Back Link */}
          <Link to="/insights" className="inline-flex items-center gap-2 text-[#2563EB] hover:text-[#3B82F6] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Insights
          </Link>
          
          {/* Header */}
          <div className="mb-12">
            <div className="inline-block bg-[#2563EB] text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
              Case Study
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-[#F8FAFC] mb-6 leading-tight">
              How a Retail SMB Increased Margins by 28%
            </h1>
            <div className="flex items-center gap-6 text-[#94A3B8]">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                January 5, 2025
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                10 min read
              </div>
            </div>
          </div>
          
          {/* Featured Image */}
          <div className="relative rounded-2xl overflow-hidden mb-12 border border-[#2563EB]/20">
            <img
              src="https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg"
              alt="Retail Success Story"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C]/60 to-transparent" />
          </div>
          
          {/* Article Content */}
          <div className="prose prose-invert prose-lg max-w-none">
            <div className="text-[#94A3B8] leading-relaxed space-y-6">
              <p className="text-xl text-[#F8FAFC] font-semibold">
                When HomeStyle Furnishings, a mid market furniture retailer with 12 locations across the Midwest, approached us in early 2024, their margins had been declining for three consecutive quarters. Here is how AI powered pricing intelligence helped them reverse course and achieve a 28% margin increase in just nine months.
              </p>
              
              <h2 className="text-3xl font-bold text-[#F8FAFC] mt-12 mb-6">The Challenge: Spreadsheet Chaos</h2>
              <p>
                HomeStyle was managing pricing for over 8,000 SKUs across multiple locations using a complex web of Excel spreadsheets. Their pricing manager, Rebecca Martinez, spent 20+ hours each week manually updating prices based on competitor research, seasonal trends, and gut feeling.
              </p>
              <p>
                <strong className="text-[#F8FAFC]">The Problems Were Compounding:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2 text-[#94A3B8]">
                <li>Competitive pricing data was always 2 to 3 weeks out of date</li>
                <li>No systematic way to test price elasticity across product categories</li>
                <li>Regional pricing differences were being ignored due to complexity</li>
                <li>Markdown decisions were reactive rather than strategic</li>
                <li>The team had no visibility into which products were margin positive vs margin negative</li>
              </ul>
              <p>
                Despite solid foot traffic and customer satisfaction scores, margins kept shrinking. The business was growing revenue but not profitability.
              </p>
              
              <h2 className="text-3xl font-bold text-[#F8FAFC] mt-12 mb-6">The Transformation: From Guesswork to Intelligence</h2>
              <p>
                In March 2024, HomeStyle implemented PriceStrike across all locations. The transformation happened in three phases, each delivering measurable impact.
              </p>
              
              <h3 className="text-2xl font-bold text-[#F8FAFC] mt-8 mb-4">Phase 1: Data Foundation (Months 1 to 2)</h3>
              <p>
                The first step was consolidating all pricing data into a single intelligent platform. PriceStrike integrated with their existing POS system and e commerce platform, automatically pulling in sales data, cost information, and inventory levels.
              </p>
              <p>
                Simultaneously, the platform began monitoring competitor pricing across 15 major competitors, tracking over 3,000 comparable products daily. For the first time, HomeStyle had real time visibility into their competitive position.
              </p>
              <p>
                <strong className="text-[#F8FAFC]">Early Discovery:</strong> The analysis revealed that HomeStyle was significantly underpricing their premium furniture lines. Customers were already choosing them for quality and service, price was not the primary decision factor for these segments. Meanwhile, they were overpriced on commodity items where customers were highly price sensitive.
              </p>
              
              <h3 className="text-2xl font-bold text-[#F8FAFC] mt-8 mb-4">Phase 2: Strategic Repricing (Months 3 to 5)</h3>
              <p>
                Armed with elasticity models and competitive intelligence, HomeStyle began strategic repricing. The AI recommended hundreds of micro adjustments, each backed by data on expected volume impact and margin change.
              </p>
              <p>
                Key moves included:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-[#94A3B8]">
                <li>Raising prices 8 to 15% on premium furniture collections where they had clear differentiation</li>
                <li>Implementing location based pricing that reflected local market conditions and competition</li>
                <li>Creating dynamic promotional strategies that protected margin while remaining competitive</li>
                <li>Optimizing markdown timing and depth based on inventory velocity and seasonal patterns</li>
              </ul>
              <p>
                The platform made it easy to simulate different scenarios before implementation. Rebecca could see projected margin impact, volume changes, and revenue outcomes for each pricing decision.
              </p>
              
              <h3 className="text-2xl font-bold text-[#F8FAFC] mt-8 mb-4">Phase 3: Optimization and Automation (Months 6 to 9)</h3>
              <p>
                By month six, the basic repricing was delivering results. But the real transformation came from automating routine decisions and optimizing the entire pricing strategy continuously.
              </p>
              <p>
                PriceStrike began automatically adjusting prices within approved guardrails based on real time market conditions. When a competitor dropped their price on a key item, the system would alert Rebecca and provide recommended responses. For commodity products, it would automatically match within defined margin thresholds.
              </p>
              <p>
                The team also implemented A/B testing for the first time, systematically learning which price points maximized profitability across different product categories and customer segments.
              </p>
              
              <h2 className="text-3xl font-bold text-[#F8FAFC] mt-12 mb-6">The Results: 28% Margin Improvement</h2>
              <p>
                By the end of nine months, HomeStyle had achieved remarkable results across multiple metrics:
              </p>
              
              <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-xl p-8 my-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-4xl font-bold text-[#2563EB] mb-2">28%</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Margin Increase</div>
                    <div className="text-sm text-[#94A3B8]">Overall gross margin improvement across all categories</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-[#2563EB] mb-2">12%</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Revenue Growth</div>
                    <div className="text-sm text-[#94A3B8]">Same store sales increase year over year</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-[#2563EB] mb-2">90%</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">Time Savings</div>
                    <div className="text-sm text-[#94A3B8]">Reduction in manual pricing work</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-[#2563EB] mb-2">4.8x</div>
                    <div className="text-[#F8FAFC] font-semibold mb-1">ROI</div>
                    <div className="text-sm text-[#94A3B8]">Return on PriceStrike investment in year one</div>
                  </div>
                </div>
              </div>
              
              <p>
                Perhaps more importantly, the quality of pricing decisions improved dramatically. Rebecca and her team shifted from spending time in spreadsheets to doing strategic analysis, identifying new opportunities, and collaborating with merchandising on assortment planning.
              </p>
              
              <h2 className="text-3xl font-bold text-[#F8FAFC] mt-12 mb-6">Key Lessons for Other SMBs</h2>
              <p>
                HomeStyle's success provides several actionable insights for other mid market retailers:
              </p>
              
              <p>
                <strong className="text-[#F8FAFC]">1. Start with Data Foundation</strong><br />
                You cannot optimize what you cannot measure. Consolidating pricing data and establishing baseline metrics is essential before making major changes.
              </p>
              
              <p>
                <strong className="text-[#F8FAFC]">2. Test and Learn</strong><br />
                The biggest wins came from systematic testing, not one time repricing. Build a culture of experimentation and continuous improvement.
              </p>
              
              <p>
                <strong className="text-[#F8FAFC]">3. Segment Your Approach</strong><br />
                Not all products or customers are the same. HomeStyle succeeded by applying different strategies to premium vs commodity products, and by recognizing regional differences.
              </p>
              
              <p>
                <strong className="text-[#F8FAFC]">4. Automate Routine, Focus on Strategy</strong><br />
                Automation freed up time for strategic work that created even more value. The goal is not to eliminate human judgment but to augment it with better data and tools.
              </p>
              
              <p>
                <strong className="text-[#F8FAFC]">5. Speed Matters</strong><br />
                In competitive markets, being able to respond quickly to market changes is a significant advantage. Real time pricing intelligence beats static analysis every time.
              </p>
              
              <h2 className="text-3xl font-bold text-[#F8FAFC] mt-12 mb-6">Looking Forward</h2>
              <p>
                HomeStyle is now planning to expand their use of pricing intelligence into markdown optimization, promotional planning, and new product pricing. They have also begun sharing insights with their buying team to inform assortment decisions.
              </p>
              <p className="text-[#F8FAFC] font-semibold">
                As Rebecca put it: "We went from guessing at prices to knowing our optimal price points. The margin improvement paid for the technology ten times over, but the real value is the confidence we now have in our pricing strategy. We are competing smarter, not just harder."
              </p>
            </div>
          </div>
          
          {/* CTA */}
          <div className="mt-16 p-8 bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-2xl">
            <h3 className="text-2xl font-bold text-[#F8FAFC] mb-4">
              Ready to Transform Your Pricing Strategy?
            </h3>
            <p className="text-[#94A3B8] mb-6">
              See how PriceStrike can help your business achieve similar results with AI powered pricing intelligence.
            </p>
            <Link to="/pricing" className="inline-block bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-4 rounded-lg font-semibold transition-colors">
              Explore Pricing Plans
            </Link>
          </div>
        </div>
      </article>
      
      <Footer />
    </div>
  );
};

export default InsightArticle3;
